import {
  DropdownButtonProps,
  DropdownSelectMultiple,
  SelectOption,
  TagDeselectItem,
  TagSelectItem,
  TagSelectSuggestion,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import { accessControl } from 'lib-shared-access-control';
import { Tag } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { PureComponent } from 'react';

import { stringToHexColor } from 'utils/color/color';

import { WithTagsInjectedProps } from '../../../hocs/tags/withTags';

import styles from './tagsSelect.module.scss';

export interface TagsSelectStateProps {
  agent: User;
}

export interface TagsSelectOwnProps {
  selectedItems: Array<Tag>;
  selectTag: (tag: Tag) => void;
  deselectTag: (tag: Tag) => void;
  allowCreatingNewTag?: boolean;
  buttonText?: string;
  buttonProps?: DropdownButtonProps['buttonProps'];
  className?: string;
  onDropdownToggle?: (isOpen: boolean, isFirstOpen?: boolean) => void;
  /** An event triggered when user toggles `Include deactivated tags checkbox` */
  onIncludeDeactivatedTagsToggle?: (include: boolean) => void;
}

export type TagsSelectProps = WithTagsInjectedProps &
  TagsSelectOwnProps &
  TagsSelectStateProps;

export interface TagWithId extends Tag {
  id: string;
}

type TagsSelectState = {
  isDropdownOpen: boolean;
};

const getDeactivatedTagIds = (tags: TagWithId[]) =>
  tags.filter((tag) => tag.is_deactivated).map((tag) => tag.id);

export class TagsSelect extends PureComponent<
  TagsSelectProps,
  TagsSelectState
> {
  static defaultProps = {
    buttonText: 'Add tag',
  };

  static addId(tag: Tag): TagWithId {
    return { ...tag, id: tag.name, name: tag.name ?? '' };
  }

  constructor(props: TagsSelectProps) {
    super(props);

    this.state = {
      isDropdownOpen: false,
    };
  }

  @boundMethod
  async createTag(name: string): Promise<void> {
    ampli.tagSelectCreateNewTagClicked({
      tagName: name,
      component: 'TagsSelect',
    });

    const {
      selectTag,
      tags: { create },
    } = this.props;

    const tag = await create({ name, color: stringToHexColor(name) });
    selectTag(tag.data);
  }

  @boundMethod
  onToggle(isOpen: boolean, isFirstOpen: boolean | undefined) {
    this.props.onDropdownToggle?.(isOpen, isFirstOpen);

    if (isFirstOpen) {
      this.setState({ isDropdownOpen: true }, () => {
        this.props.tags.filterByChange('active');
      });
    } else if (isOpen) {
      this.setState({ isDropdownOpen: true });
    } else if (!isOpen && this.props.tags.searchTerm !== '') {
      this.setState({ isDropdownOpen: false }, () => {
        this.props.tags.searchTermChange('');
      });
    } else if (!isOpen) {
      this.setState({ isDropdownOpen: false });
    }
  }

  @boundMethod
  toggleDeactivatedTags(): void {
    const { tags, onIncludeDeactivatedTagsToggle } = this.props;

    onIncludeDeactivatedTagsToggle?.(tags.filterBy === 'active');
    tags.filterByChange(tags.filterBy === 'active' ? null : 'active');
  }

  renderSuggestion(search: string): JSX.Element {
    return (
      <div className={styles.suggestion}>
        <div
          className={styles.tagColor}
          style={{ backgroundColor: stringToHexColor(search) }}
        />
        <div>Create &quot;{search}&quot; tag</div>
      </div>
    );
  }

  render() {
    const {
      tags,
      selectedItems,
      selectTag,
      deselectTag,
      buttonText,
      allowCreatingNewTag = false,
      buttonProps,
      className,
    } = this.props;

    const {
      items,
      searchTermChange,
      getItemsAsyncStatus,
      searchTerm,
      hasNextPage,
      hasPreviousPage,
      nextPage,
      previousPage,
      filterBy,
    } = tags;

    const tagsWithId = items.map(TagsSelect.addId);
    const selectedTagsWithId = selectedItems.map(TagsSelect.addId);
    const tooltipItems = [...tagsWithId, ...selectedTagsWithId];
    const filteredTags = tagsWithId.filter(
      (tag) =>
        !selectedTagsWithId.filter((selectedTag) => tag.id === selectedTag.id)
          .length,
    );
    const disabledTags = [
      ...getDeactivatedTagIds(tagsWithId),
      ...getDeactivatedTagIds(selectedTagsWithId),
    ];
    const createTagAllowedAndPermitted =
      allowCreatingNewTag && accessControl.hasAccess('settings.tag');

    const shouldShowSuggestion =
      searchTerm.trim().length > 1 &&
      createTagAllowedAndPermitted &&
      !items.some(
        ({ name }) => name.toLowerCase() === searchTerm.toLowerCase(),
      );

    return (
      <DropdownSelectMultiple
        className={classnames(styles.tagsWrapper, className)}
        selectedItems={selectedTagsWithId}
        onRemove={deselectTag}
        containerProps={{ className: styles.tagsSelectContainer }}
        items={filteredTags}
        itemComponent={TagSelectItem}
        tooltipItems={Object.assign(
          {},
          ...tooltipItems.map((t) => ({
            [t.id]: { content: t.name },
          })),
        )}
        onSearch={searchTermChange}
        suggestionComponent={
          shouldShowSuggestion ? TagSelectSuggestion : undefined
        }
        renderSuggestion={this.renderSuggestion}
        onSuggest={this.createTag}
        onSelect={selectTag}
        onToggle={this.onToggle}
        keepOpen
        hasSearch
        noArrow={false}
        previewItemsNumber={3}
        buttonProps={{
          isLoading: getItemsAsyncStatus === 'loading',
          variant: 'tertiary',
          ...buttonProps,
        }}
        emptyMessage={searchTerm ? 'No tags found' : 'No tags available'}
        buttonText={buttonText}
        MoreItemsItemComponent={TagDeselectItem}
        pagination={{
          nextEnable: hasNextPage,
          previousEnable: hasPreviousPage,
          onNext: nextPage,
          onPrevious: previousPage,
        }}
        disabledItems={disabledTags}
        optionComponent={
          <SelectOption
            name="Include deactivated tags"
            checked={filterBy === null}
            onChange={this.toggleDeactivatedTags}
          />
        }
        icon="tag"
      />
    );
  }
}
